'use strict';

var findExtension = require('../Helper').findExtension,
    findExtensions = require('../Helper').findExtensions,
    findFlowableErrorEventDefinition = require('../Helper').findFlowableErrorEventDefinition;

var handleLegacyScopes = require('../util/handleLegacyScopes');

var createFlowableExecutionListenerScript = require('../CreateHelper').createFlowableExecutionListenerScript,
    createFlowableFieldInjection = require('../CreateHelper').createFlowableFieldInjection,
    createFlowableIn = require('../CreateHelper').createFlowableIn,
    createFlowableInWithBusinessKey = require('../CreateHelper').createFlowableInWithBusinessKey,
    createFlowableOut = require('../CreateHelper').createFlowableOut,
    createFlowableProperty = require('../CreateHelper').createFlowableProperty,
    createInputParameter = require('../CreateHelper').createInputParameter,
    createOutputParameter = require('../CreateHelper').createOutputParameter,
    createFlowableErrorEventDefinition = require('../CreateHelper').createFlowableErrorEventDefinition,
    createError = require('../CreateHelper').createError;

var EventDefinitionHelper = require('../../../../helper/EventDefinitionHelper');

var getRoot = require('bpmn-js-properties-panel/lib/Utils').getRoot;

var getBusinessObject = require('bpmn-js/lib/util/ModelUtil').getBusinessObject;

var is = require('bpmn-js/lib/util/ModelUtil').is,
    isAny = require('bpmn-js/lib/features/modeling/util/ModelingUtil').isAny;

var find = require('lodash/find'),
    forEach = require('lodash/forEach'),
    isString = require('lodash/isString'),
    keys = require('lodash/keys'),
    remove = require('lodash/remove');

var FLOWABLE_SERVICE_TASK_LIKE = [
  'flowable:class',
  'flowable:delegateExpression',
  'flowable:expression'
];

/**
 * Applies an element template to an element. Sets `flowable:modelerTemplate` and
 * `flowable:modelerTemplateVersion`.
 */
function ChangeElementTemplateHandler(bpmnFactory, commandStack, modeling) {
  this._bpmnFactory = bpmnFactory;
  this._commandStack = commandStack;
  this._modeling = modeling;
}

ChangeElementTemplateHandler.$inject = [
  'bpmnFactory',
  'commandStack',
  'modeling'
];

module.exports = ChangeElementTemplateHandler;

/**
   * Change an element's template and update its properties as specified in `newTemplate`. Specify
   * `oldTemplate` to update from one template to another. If `newTemplate` isn't specified the
   * `flowable:modelerTemplate` and `flowable:modelerTemplateVersion` properties will be removed from
   * the element.
   *
   * @param {Object} context
   * @param {Object} context.element
   * @param {Object} [context.oldTemplate]
   * @param {Object} [context.newTemplate]
   */
ChangeElementTemplateHandler.prototype.preExecute = function(context) {
  var element = context.element,
      newTemplate = context.newTemplate,
      oldTemplate = context.oldTemplate;

  var self = this;

  // Update flowable:modelerTemplate attribute
  this._updateFlowableModelerTemplate(element, newTemplate);

  if (newTemplate) {

    // Update properties
    this._updateProperties(element, oldTemplate, newTemplate);

    // Update flowable:ExecutionListener properties
    this._updateFlowableExecutionListenerProperties(element, newTemplate);

    // Update flowable:Field properties
    this._updateFlowableFieldProperties(element, oldTemplate, newTemplate);

    // Update flowable:In and flowable:Out properties
    this._updateFlowableInOutProperties(element, oldTemplate, newTemplate);

    // Update flowable:InputParameter and flowable:OutputParameter properties
    this._updateFlowableInputOutputParameterProperties(element, oldTemplate, newTemplate);

    // Update flowable:Property properties
    this._updateFlowablePropertyProperties(element, oldTemplate, newTemplate);

    // Update flowable:ErrorEventDefinition properties
    this._updateFlowableErrorEventDefinitionProperties(element, oldTemplate, newTemplate);

    // Update properties for each scope
    forEach(handleLegacyScopes(newTemplate.scopes), function(newScopeTemplate) {
      self._updateScopeProperties(element, oldTemplate, newScopeTemplate, newTemplate);
    });

  }
};

ChangeElementTemplateHandler.prototype._getOrCreateExtensionElements = function(element) {
  var bpmnFactory = this._bpmnFactory,
      modeling = this._modeling;

  var businessObject = getBusinessObject(element);

  var extensionElements = businessObject.get('extensionElements');

  if (!extensionElements) {
    extensionElements = bpmnFactory.create('bpmn:ExtensionElements', {
      values: []
    });

    extensionElements.$parent = businessObject;

    modeling.updateProperties(element, {
      extensionElements: extensionElements
    });
  }

  return extensionElements;
};

/**
 * Update `flowable:ErrorEventDefinition` properties of specified business object. Event
 * definitions can only exist in `bpmn:ExtensionElements`.
 *
 * Ensures an bpmn:Error exists for the event definition.
 *
 * @param {djs.model.Base} element
 * @param {Object} oldTemplate
 * @param {Object} newTemplate
 */
ChangeElementTemplateHandler.prototype._updateFlowableErrorEventDefinitionProperties = function(element, oldTemplate, newTemplate) {
  var bpmnFactory = this._bpmnFactory,
      commandStack = this._commandStack;

  var newProperties = newTemplate.properties.filter(function(newProperty) {
    var newBinding = newProperty.binding,
        newBindingType = newBinding.type;

    return newBindingType === 'flowable:errorEventDefinition';
  });

  // (1) Do not override if no updates
  if (!newProperties.length) {
    return;
  }

  var businessObject = this._getOrCreateExtensionElements(element);

  var oldErrorEventDefinitions = findExtensions(element, [ 'flowable:ErrorEventDefinition' ]);

  newProperties.forEach(function(newProperty) {
    var oldProperty = findOldProperty(oldTemplate, newProperty),
        oldEventDefinition = oldProperty && findOldBusinessObject(businessObject, oldProperty),
        newBinding = newProperty.binding;

    // (2) Update old event definitions
    if (oldProperty && oldEventDefinition) {

      if (!propertyChanged(oldEventDefinition, oldProperty)) {
        commandStack.execute('properties-panel.update-businessobject', {
          element: element,
          businessObject: oldEventDefinition,
          properties: {
            expression: newProperty.value
          }
        });
      }

      remove(oldErrorEventDefinitions, oldEventDefinition);
    }

    // (3) Create new event definition + error
    else {
      var rootElement = getRoot(getBusinessObject(element)),
          newError = createError(newBinding.errorRef, rootElement, bpmnFactory),
          newEventDefinition =
            createFlowableErrorEventDefinition(newBinding, newProperty.value, newError, businessObject, bpmnFactory);

      commandStack.execute('properties-panel.update-businessobject-list', {
        element: element,
        currentObject: rootElement,
        propertyName: 'rootElements',
        objectsToAdd: [ newError ],
        objectsToRemove: []
      });

      commandStack.execute('properties-panel.update-businessobject-list', {
        element: element,
        currentObject: businessObject,
        propertyName: 'values',
        objectsToAdd: [ newEventDefinition ],
        objectsToRemove: []
      });
    }

  });

  // (4) Remove old event definitions
  if (oldErrorEventDefinitions.length) {
    commandStack.execute('properties-panel.update-businessobject-list', {
      element: element,
      currentObject: businessObject,
      propertyName: 'values',
      objectsToAdd: [],
      objectsToRemove: oldErrorEventDefinitions
    });
  }
};

/**
 * Update `flowable:ExecutionListener` properties of specified business object. Execution listeners
 * will always be overridden. Execution listeners can only exist in `bpmn:ExtensionElements`.
 *
 * @param {djs.model.Base} element
 * @param {Object} newTemplate
 */
ChangeElementTemplateHandler.prototype._updateFlowableExecutionListenerProperties = function(element, newTemplate) {
  var bpmnFactory = this._bpmnFactory,
      commandStack = this._commandStack;

  var newProperties = newTemplate.properties.filter(function(newProperty) {
    var newBinding = newProperty.binding,
        newBindingType = newBinding.type;

    return newBindingType === 'flowable:executionListener';
  });

  // (1) Do not override old execution listeners if no new execution listeners specified
  if (!newProperties.length) {
    return;
  }

  var businessObject = this._getOrCreateExtensionElements(element);

  // (2) Remove old execution listeners
  var oldExecutionListeners = findExtensions(element, [ 'flowable:ExecutionListener' ]);

  // (3) Add new execution listeners
  var newExecutionListeners = newProperties.map(function(newProperty) {
    var newBinding = newProperty.binding,
        propertyValue = newProperty.value;

    return createFlowableExecutionListenerScript(newBinding, propertyValue, bpmnFactory);
  });

  commandStack.execute('properties-panel.update-businessobject-list', {
    element: element,
    currentObject: businessObject,
    propertyName: 'values',
    objectsToAdd: newExecutionListeners,
    objectsToRemove: oldExecutionListeners
  });
};

/**
 * Update `flowable:Field` properties of specified business object.
 * If business object is `flowable:ExecutionListener` or `flowable:TaskListener` `fields` property
 * will be updated. Otherwise `extensionElements.values` property will be updated.
 *
 * @param {djs.model.Base} element
 * @param {Object} oldTemplate
 * @param {Object} newTemplate
 * @param {ModdleElement} businessObject
 */
ChangeElementTemplateHandler.prototype._updateFlowableFieldProperties = function(element, oldTemplate, newTemplate, businessObject) {
  var bpmnFactory = this._bpmnFactory,
      commandStack = this._commandStack;

  var newProperties = newTemplate.properties.filter(function(newProperty) {
    var newBinding = newProperty.binding,
        newBindingType = newBinding.type;

    return newBindingType === 'flowable:field';
  });

  // (1) Do not override old fields if no new fields specified
  if (!newProperties.length) {
    return;
  }

  if (!businessObject) {
    businessObject = this._getOrCreateExtensionElements(element);
  }

  var propertyName = isAny(businessObject, [ 'flowable:ExecutionListener', 'flowable:TaskListener' ])
    ? 'fields'
    : 'values';

  var oldFields = findExtensions(element, [ 'flowable:Field' ]);

  newProperties.forEach(function(newProperty) {
    var oldProperty = findOldProperty(oldTemplate, newProperty),
        oldField = oldProperty && findOldBusinessObject(businessObject, oldProperty),
        newBinding = newProperty.binding;

    // (2) Update old fields
    if (oldProperty && oldField) {

      if (!propertyChanged(oldField, oldProperty)) {
        commandStack.execute('properties-panel.update-businessobject', {
          element: element,
          businessObject: oldField,
          properties: {
            string: newProperty.value
          }
        });
      }

      remove(oldFields, oldField);
    }

    // (3) Add new fields
    else {
      commandStack.execute('properties-panel.update-businessobject-list', {
        element: element,
        currentObject: businessObject,
        propertyName: propertyName,
        objectsToAdd: [ createFlowableFieldInjection(newBinding, newProperty.value, bpmnFactory) ],
        objectsToRemove: []
      });
    }
  });

  // (4) Remove old fields
  if (oldFields.length) {
    commandStack.execute('properties-panel.update-businessobject-list', {
      element: element,
      currentObject: businessObject,
      propertyName: propertyName,
      objectsToAdd: [],
      objectsToRemove: oldFields
    });
  }
};

/**
 * Update `flowable:In` and `flowable:Out` properties of specified business object. Only
 * `bpmn:CallActivity` and events with `bpmn:SignalEventDefinition` can have ins. Only
 * `flowable:CallActivity` can have outs.
 *
 * @param {djs.model.Base} element
 * @param {Object} oldTemplate
 * @param {Object} newTemplate
 */
ChangeElementTemplateHandler.prototype._updateFlowableInOutProperties = function(element, oldTemplate, newTemplate) {
  var bpmnFactory = this._bpmnFactory,
      commandStack = this._commandStack;

  var newProperties = newTemplate.properties.filter(function(newProperty) {
    var newBinding = newProperty.binding,
        newBindingType = newBinding.type;

    return newBindingType === 'flowable:in'
      || newBindingType === 'flowable:in:businessKey'
      || newBindingType === 'flowable:out';
  });

  // (1) Do not override old fields if no new fields specified
  if (!newProperties.length) {
    return;
  }

  // Get extension elements of either signal event definition or call activity
  var businessObject = this._getOrCreateExtensionElements(
    EventDefinitionHelper.getSignalEventDefinition(element) || element);

  var oldInsAndOuts = findExtensions(businessObject, [ 'flowable:In', 'flowable:Out' ]);

  newProperties.forEach(function(newProperty) {
    var oldProperty = findOldProperty(oldTemplate, newProperty),
        oldBinding = oldProperty && oldProperty.binding,
        oldInOurOut = oldProperty && findOldBusinessObject(businessObject, oldProperty),
        newPropertyValue = newProperty.value,
        newBinding = newProperty.binding,
        newBindingType = newBinding.type,
        newInOrOut,
        properties = {};

    // (2) Update old ins and outs
    if (oldProperty && oldInOurOut) {

      if (!propertyChanged(oldInOurOut, oldProperty)) {
        if (newBindingType === 'flowable:in') {
          if (newBinding.expression) {
            properties[ 'flowable:sourceExpression' ] = newPropertyValue;
          } else {
            properties[ 'flowable:source' ] = newPropertyValue;
          }
        } else if (newBindingType === 'flowable:in:businessKey') {
          properties[ 'flowable:businessKey' ] = newPropertyValue;
        } else if (newBindingType === 'flowable:out') {
          properties[ 'flowable:target' ] = newPropertyValue;
        }
      }

      // Update `flowable:local` property if it changed
      if ((oldBinding.local && !newBinding.local) || !oldBinding.local && newBinding.local) {
        properties.local = newBinding.local;
      }

      if (keys(properties)) {
        commandStack.execute('properties-panel.update-businessobject', {
          element: element,
          businessObject: oldInOurOut,
          properties: properties
        });
      }

      remove(oldInsAndOuts, oldInOurOut);
    }

    // (3) Add new ins and outs
    else {
      if (newBindingType === 'flowable:in') {
        newInOrOut = createFlowableIn(newBinding, newPropertyValue, bpmnFactory);
      } else if (newBindingType === 'flowable:out') {
        newInOrOut = createFlowableOut(newBinding, newPropertyValue, bpmnFactory);
      } else if (newBindingType === 'flowable:in:businessKey') {
        newInOrOut = createFlowableInWithBusinessKey(newBinding, newPropertyValue, bpmnFactory);
      }

      commandStack.execute('properties-panel.update-businessobject-list', {
        element: element,
        currentObject: businessObject,
        propertyName: 'values',
        objectsToAdd: [ newInOrOut ],
        objectsToRemove: []
      });
    }
  });

  // (4) Remove old ins and outs
  if (oldInsAndOuts.length) {
    commandStack.execute('properties-panel.update-businessobject-list', {
      element: element,
      currentObject: businessObject,
      propertyName: 'values',
      objectsToAdd: [],
      objectsToRemove: oldInsAndOuts
    });
  }
};

/**
 * Update `flowable:InputParameter` and `flowable:OutputParameter` properties of specified business
 * object. Both can only exist in `flowable:InputOutput` which can exist in `bpmn:ExtensionElements`
 * or `flowable:Connector`.
 *
 * @param {djs.model.Base} element
 * @param {Object} oldTemplate
 * @param {Object} newTemplate
 */
ChangeElementTemplateHandler.prototype._updateFlowableInputOutputParameterProperties = function(element, oldTemplate, newTemplate, businessObject) {
  var bpmnFactory = this._bpmnFactory,
      commandStack = this._commandStack;

  var newProperties = newTemplate.properties.filter(function(newProperty) {
    var newBinding = newProperty.binding,
        newBindingType = newBinding.type;

    return newBindingType === 'flowable:inputParameter' || newBindingType === 'flowable:outputParameter';
  });

  // (1) Do not override old inputs and outputs if no new inputs and outputs specified
  if (!newProperties.length) {
    return;
  }

  if (!businessObject) {
    businessObject = this._getOrCreateExtensionElements(element);
  }

  var inputOutput;

  if (is(businessObject, 'flowable:Connector')) {
    inputOutput = businessObject.get('flowable:inputOutput');

    if (!inputOutput) {
      inputOutput = bpmnFactory.create('flowable:InputOutput');

      commandStack.execute('properties-panel.update-businessobject', {
        element: element,
        businessObject: businessObject,
        properties: {
          inputOutput: inputOutput
        }
      });
    }
  } else {
    inputOutput = findExtension(businessObject, 'flowable:InputOutput');

    if (!inputOutput) {
      inputOutput = bpmnFactory.create('flowable:InputOutput');

      commandStack.execute('properties-panel.update-businessobject-list', {
        element: element,
        currentObject: businessObject,
        propertyName: 'values',
        objectsToAdd: [ inputOutput ],
        objectsToRemove: []
      });
    }
  }

  var oldInputs = inputOutput.get('flowable:inputParameters')
    ? inputOutput.get('flowable:inputParameters').slice()
    : [];

  var oldOutputs = inputOutput.get('flowable:outputParameters')
    ? inputOutput.get('flowable:outputParameters').slice()
    : [];

  var propertyName;

  newProperties.forEach(function(newProperty) {
    var oldProperty = findOldProperty(oldTemplate, newProperty),
        oldInputOrOutput = oldProperty && findOldBusinessObject(businessObject, oldProperty),
        newPropertyValue = newProperty.value,
        newBinding = newProperty.binding,
        newBindingType = newBinding.type;

    var newInputOrOutput,
        properties;

    // (2) Update old inputs and outputs
    if (oldProperty && oldInputOrOutput) {

      if (!propertyChanged(oldInputOrOutput, oldProperty)) {
        if (is(oldInputOrOutput, 'flowable:InputParameter')) {
          properties = {
            value: newPropertyValue
          };
        } else {
          properties = {
            name: newPropertyValue
          };
        }

        commandStack.execute('properties-panel.update-businessobject', {
          element: element,
          businessObject: oldInputOrOutput,
          properties: properties
        });
      }

      if (is(oldInputOrOutput, 'flowable:InputParameter')) {
        remove(oldInputs, oldInputOrOutput);
      } else {
        remove(oldOutputs, oldInputOrOutput);
      }
    }

    // (3) Add new inputs and outputs
    else {
      if (newBindingType === 'flowable:inputParameter') {
        propertyName = 'inputParameters';

        newInputOrOutput = createInputParameter(newBinding, newPropertyValue, bpmnFactory);
      } else {
        propertyName = 'outputParameters';

        newInputOrOutput = createOutputParameter(newBinding, newPropertyValue, bpmnFactory);
      }

      commandStack.execute('properties-panel.update-businessobject-list', {
        element: element,
        currentObject: inputOutput,
        propertyName: propertyName,
        objectsToAdd: [ newInputOrOutput ],
        objectsToRemove: []
      });
    }
  });

  // (4) Remove old inputs and outputs
  if (oldInputs.length) {
    commandStack.execute('properties-panel.update-businessobject-list', {
      element: element,
      currentObject: inputOutput,
      propertyName: 'inputParameters',
      objectsToAdd: [],
      objectsToRemove: oldInputs
    });
  }

  if (oldOutputs.length) {
    commandStack.execute('properties-panel.update-businessobject-list', {
      element: element,
      currentObject: inputOutput,
      propertyName: 'outputParameters',
      objectsToAdd: [],
      objectsToRemove: oldOutputs
    });
  }
};

ChangeElementTemplateHandler.prototype._updateFlowableModelerTemplate = function(element, newTemplate) {
  var modeling = this._modeling;

  modeling.updateProperties(element, {
    'flowable:modelerTemplate': newTemplate && newTemplate.id,
    'flowable:modelerTemplateVersion': newTemplate && newTemplate.version
  });
};

/**
 * Update `flowable:Property` properties of specified business object. `flowable:Property` can only
 * exist in `flowable:Properties`.
 *
 * @param {djs.model.Base} element
 * @param {Object} oldTemplate
 * @param {Object} newTemplate
 * @param {ModdleElement} businessObject
 */
ChangeElementTemplateHandler.prototype._updateFlowablePropertyProperties = function(element, oldTemplate, newTemplate, businessObject) {
  var bpmnFactory = this._bpmnFactory,
      commandStack = this._commandStack;

  var newProperties = newTemplate.properties.filter(function(newProperty) {
    var newBinding = newProperty.binding,
        newBindingType = newBinding.type;

    return newBindingType === 'flowable:property';
  });

  // (1) Do not override old properties if no new properties specified
  if (!newProperties.length) {
    return;
  }

  if (businessObject) {
    businessObject = this._getOrCreateExtensionElements(businessObject);
  } else {
    businessObject = this._getOrCreateExtensionElements(element);
  }

  var flowableProperties = findExtension(businessObject, 'flowable:Properties');

  if (!flowableProperties) {
    flowableProperties = bpmnFactory.create('flowable:Properties');

    commandStack.execute('properties-panel.update-businessobject-list', {
      element: element,
      currentObject: businessObject,
      propertyName: 'values',
      objectsToAdd: [ flowableProperties ],
      objectsToRemove: []
    });
  }

  var oldFlowableProperties = flowableProperties.get('flowable:values')
    ? flowableProperties.get('flowable:values').slice()
    : [];

  newProperties.forEach(function(newProperty) {
    var oldProperty = findOldProperty(oldTemplate, newProperty),
        oldFlowableProperty = oldProperty && findOldBusinessObject(businessObject, oldProperty),
        newPropertyValue = newProperty.value,
        newBinding = newProperty.binding;

    // (2) Update old properties
    if (oldProperty && oldFlowableProperty) {

      if (!propertyChanged(oldFlowableProperty, oldProperty)) {
        commandStack.execute('properties-panel.update-businessobject', {
          element: element,
          businessObject: oldFlowableProperty,
          properties: {
            value: newPropertyValue
          }
        });
      }

      remove(oldFlowableProperties, oldFlowableProperty);
    }

    // (3) Add new properties
    else {
      commandStack.execute('properties-panel.update-businessobject-list', {
        element: element,
        currentObject: flowableProperties,
        propertyName: 'values',
        objectsToAdd: [ createFlowableProperty(newBinding, newPropertyValue, bpmnFactory) ],
        objectsToRemove: []
      });
    }
  });

  // (4) Remove old properties
  if (oldFlowableProperties.length) {
    commandStack.execute('properties-panel.update-businessobject-list', {
      element: element,
      currentObject: flowableProperties,
      propertyName: 'values',
      objectsToAdd: [],
      objectsToRemove: oldFlowableProperties
    });
  }
};

/**
 * Update `bpmn:conditionExpression` property of specified element. Since condition expression is
 * is not primitive it needs special handling.
 *
 * @param {djs.model.Base} element
 * @param {Object} oldProperty
 * @param {Object} newProperty
 */
ChangeElementTemplateHandler.prototype._updateConditionExpression = function(element, oldProperty, newProperty) {
  var bpmnFactory = this._bpmnFactory,
      commandStack = this._commandStack,
      modeling = this._modeling;

  var newBinding = newProperty.binding,
      newPropertyValue = newProperty.value;

  if (!oldProperty) {
    modeling.updateProperties(element, {
      conditionExpression: bpmnFactory.create('bpmn:FormalExpression', {
        body: newPropertyValue,
        language: newBinding.scriptFormat
      })
    });

    return;
  }

  var oldBinding = oldProperty.binding,
      oldPropertyValue = oldProperty.value;

  var businessObject = getBusinessObject(element),
      conditionExpression = businessObject.get('bpmn:conditionExpression');

  var properties = {};

  if (conditionExpression.get('body') === oldPropertyValue) {
    properties.body = newPropertyValue;
  }

  if (conditionExpression.get('language') === oldBinding.scriptFormat) {
    properties.language = newBinding.scriptFormat;
  }

  if (!keys(properties).length) {
    return;
  }

  commandStack.execute('properties-panel.update-businessobject', {
    element: element,
    businessObject: conditionExpression,
    properties: properties
  });
};

ChangeElementTemplateHandler.prototype._updateProperties = function(element, oldTemplate, newTemplate, businessObject) {
  var self = this;

  var commandStack = this._commandStack;

  var newProperties = newTemplate.properties.filter(function(newProperty) {
    var newBinding = newProperty.binding,
        newBindingType = newBinding.type;

    return newBindingType === 'property';
  });

  if (!newProperties.length) {
    return;
  }

  if (!businessObject) {
    businessObject = getBusinessObject(element);
  }

  newProperties.forEach(function(newProperty) {
    var oldProperty = findOldProperty(oldTemplate, newProperty),
        newBinding = newProperty.binding,
        newBindingName = newBinding.name,
        newPropertyValue = newProperty.value,
        changedElement,
        properties;

    if (newBindingName === 'conditionExpression') {
      self._updateConditionExpression(element, oldProperty, newProperty);
    } else {

      if (is(businessObject, 'bpmn:Error')) {
        changedElement = businessObject;
      } else {
        changedElement = element;
      }

      if (oldProperty && propertyChanged(changedElement, oldProperty)) {
        return;
      }

      properties = {};

      properties[ newBindingName ] = newPropertyValue;

      // Only one of `flowable:class`, `flowable:delegateExpression` and `flowable:expression` can be
      // set
      // TODO(philippfromme): ensuring only one of these properties is set at a time should be
      // implemented in a behavior and not in this handler and properties panel UI
      if (FLOWABLE_SERVICE_TASK_LIKE.indexOf(newBindingName) !== -1) {
        FLOWABLE_SERVICE_TASK_LIKE.forEach(function(flowableServiceTaskLikeProperty) {
          if (flowableServiceTaskLikeProperty !== newBindingName) {
            properties[ flowableServiceTaskLikeProperty ] = undefined;
          }
        });
      }

      commandStack.execute('properties-panel.update-businessobject', {
        element: element,
        businessObject: businessObject,
        properties: properties
      });
    }
  });
};

/**
 * Update properties for a specified scope.
 *
 * @param {djs.model.Base} element
 * @param {Object} oldTemplate
 * @param {Object} newScopeTemplate
 * @param {Object} newTemplate
 */
ChangeElementTemplateHandler.prototype._updateScopeProperties = function(element, oldTemplate, newScopeTemplate, newTemplate) {
  var bpmnFactory = this._bpmnFactory,
      commandStack = this._commandStack;

  var scopeName = newScopeTemplate.type;

  var scopeElement;

  scopeElement = findOldScopeElement(element, newScopeTemplate, newTemplate);

  if (!scopeElement) {

    scopeElement = bpmnFactory.create(scopeName);
  }

  var oldScopeTemplate = findOldScopeTemplate(newScopeTemplate, oldTemplate);

  // Update properties
  this._updateProperties(element, oldScopeTemplate, newScopeTemplate, scopeElement);

  // Update flowable:ExecutionListener properties
  this._updateFlowableExecutionListenerProperties(element, newScopeTemplate);

  // Update flowable:In and flowable:Out properties
  this._updateFlowableInOutProperties(element, oldScopeTemplate, newScopeTemplate);

  // Update flowable:InputParameter and flowable:OutputParameter properties
  this._updateFlowableInputOutputParameterProperties(element, oldScopeTemplate, newScopeTemplate, scopeElement);

  // Update flowable:Field properties
  this._updateFlowableFieldProperties(element, oldScopeTemplate, newScopeTemplate, scopeElement);

  // Update flowable:Property properties
  this._updateFlowablePropertyProperties(element, oldScopeTemplate, newScopeTemplate, scopeElement);

  // Assume: root elements were already been created in root by referenced event
  // definition binding
  if (isRootElementScope(scopeName)) {
    return;
  }

  var extensionElements = this._getOrCreateExtensionElements(element);

  commandStack.execute('properties-panel.update-businessobject-list', {
    element: element,
    currentObject: extensionElements,
    propertyName: 'values',
    objectsToAdd: [ scopeElement ],
    objectsToRemove: []
  });
};

// helpers //////////

/**
 * Find old business object matching specified old property.
 *
 * @param {djs.model.Base|ModdleElement} element
 * @param {Object} oldProperty
 *
 * @returns {ModdleElement}
 */
function findOldBusinessObject(element, oldProperty) {
  var businessObject = getBusinessObject(element),
      propertyName;

  var oldBinding = oldProperty.binding,
      oldBindingType = oldBinding.type;

  if (oldBindingType === 'flowable:field') {

    if (isAny(businessObject, [ 'flowable:ExecutionListener', 'flowable:TaskListener' ])) {
      propertyName = 'flowable:fields';
    } else {
      propertyName = 'bpmn:values';
    }

    if (!businessObject || !businessObject.get(propertyName) || !businessObject.get(propertyName).length) {
      return;
    }

    return find(businessObject.get(propertyName), function(oldBusinessObject) {
      return oldBusinessObject.get('flowable:name') === oldBinding.name;
    });
  }

  if (oldBindingType === 'flowable:in') {
    return find(businessObject.get('values'), function(oldBusinessObject) {
      return oldBusinessObject.get('target') === oldBinding.target;
    });
  }

  if (oldBindingType === 'flowable:in:businessKey') {
    return find(businessObject.get('values'), function(oldBusinessObject) {
      return isString(oldBusinessObject.get('businessKey'));
    });
  }

  if (oldBindingType === 'flowable:out') {
    return find(businessObject.get('values'), function(oldBusinessObject) {
      return oldBusinessObject.get('source') === oldBinding.source ||
        oldBusinessObject.get('sourceExpression') || oldBinding.sourceExpression;
    });
  }

  if (oldBindingType === 'flowable:inputParameter' || oldBindingType === 'flowable:outputParameter') {

    if (is(businessObject, 'flowable:Connector')) {
      businessObject = businessObject.get('flowable:inputOutput');

      if (!businessObject) {
        return;
      }
    } else {
      businessObject = findExtension(businessObject, 'flowable:InputOutput');

      if (!businessObject) {
        return;
      }
    }

    if (oldBindingType === 'flowable:inputParameter') {
      return find(businessObject.get('flowable:inputParameters'), function(oldBusinessObject) {
        return oldBusinessObject.get('flowable:name') === oldBinding.name;
      });
    } else {
      return find(businessObject.get('flowable:outputParameters'), function(oldBusinessObject) {
        var definition;

        if (oldBinding.scriptFormat) {
          definition = oldBusinessObject.get('flowable:definition');

          return definition && definition.get('flowable:value') === oldBinding.source;
        } else {
          return oldBusinessObject.get('flowable:value') === oldBinding.source;
        }
      });
    }

  }

  if (oldBindingType === 'flowable:property') {
    if (!businessObject || !businessObject.get('values') || !businessObject.get('values').length) {
      return;
    }

    businessObject = findExtension(businessObject, 'flowable:Properties');

    if (!businessObject) {
      return;
    }

    return find(businessObject.get('values'), function(oldBusinessObject) {
      return oldBusinessObject.get('flowable:name') === oldBinding.name;
    });
  }

  if (oldBindingType === 'flowable:errorEventDefinition') {
    return findFlowableErrorEventDefinition(element, oldBinding.errorRef);
  }
}

/**
 * Find old property matching specified new property.
 *
 * @param {Object} oldTemplate
 * @param {Object} newProperty
 *
 * @returns {Object}
 */
function findOldProperty(oldTemplate, newProperty) {
  if (!oldTemplate) {
    return;
  }

  var oldProperties = oldTemplate.properties,
      newBinding = newProperty.binding,
      newBindingName = newBinding.name,
      newBindingType = newBinding.type;

  if (newBindingType === 'property') {
    return find(oldProperties, function(oldProperty) {
      var oldBinding = oldProperty.binding,
          oldBindingName = oldBinding.name,
          oldBindingType = oldBinding.type;

      return oldBindingType === 'property' && oldBindingName === newBindingName;
    });
  }

  if (newBindingType === 'flowable:field') {
    return find(oldProperties, function(oldProperty) {
      var oldBinding = oldProperty.binding,
          oldBindingName = oldBinding.name,
          oldBindingType = oldBinding.type;

      return oldBindingType === 'flowable:field' && oldBindingName === newBindingName;
    });
  }

  if (newBindingType === 'flowable:in') {
    return find(oldProperties, function(oldProperty) {
      var oldBinding = oldProperty.binding,
          oldBindingType = oldBinding.type;

      if (oldBindingType !== 'flowable:in') {
        return;
      }

      // Always override if change from source to source expression or vice versa
      if ((oldBinding.expression && !newBinding.expression) ||
        !oldBinding.expression && newBinding.expression) {
        return;
      }

      return oldBinding.target === newBinding.target;
    });
  }

  if (newBindingType === 'flowable:in:businessKey') {
    return find(oldProperties, function(oldProperty) {
      var oldBinding = oldProperty.binding,
          oldBindingType = oldBinding.type;

      return oldBindingType === 'flowable:in:businessKey';
    });
  }

  if (newBindingType === 'flowable:out') {
    return find(oldProperties, function(oldProperty) {
      var oldBinding = oldProperty.binding,
          oldBindingType = oldBinding.type;

      return oldBindingType === 'flowable:out' && (
        oldBinding.source === newBinding.source ||
        oldBinding.sourceExpression === newBinding.sourceExpression
      );
    });
  }

  if (newBindingType === 'flowable:inputParameter') {
    return find(oldProperties, function(oldProperty) {
      var oldBinding = oldProperty.binding,
          oldBindingName = oldBinding.name,
          oldBindingType = oldBinding.type;

      if (oldBindingType !== 'flowable:inputParameter') {
        return;
      }

      return oldBindingName === newBindingName
        && oldBinding.scriptFormat === newBinding.scriptFormat;
    });
  }

  if (newBindingType === 'flowable:outputParameter') {
    return find(oldProperties, function(oldProperty) {
      var oldBinding = oldProperty.binding,
          oldBindingType = oldBinding.type;

      if (oldBindingType !== 'flowable:outputParameter') {
        return;
      }

      return oldBinding.source === newBinding.source
        && oldBinding.scriptFormat === newBinding.scriptFormat;
    });
  }

  if (newBindingType === 'flowable:property') {
    return find(oldProperties, function(oldProperty) {
      var oldBinding = oldProperty.binding,
          oldBindingName = oldBinding.name,
          oldBindingType = oldBinding.type;

      return oldBindingType === 'flowable:property' && oldBindingName === newBindingName;
    });
  }

  if (newBindingType === 'flowable:errorEventDefinition') {
    return find(oldProperties, function(oldProperty) {
      var newBindingRef = newBinding.errorRef,
          oldBinding = oldProperty.binding,
          oldBindingRef = oldBinding.errorRef,
          oldBindingType = oldBinding.type;

      return oldBindingType === 'flowable:errorEventDefinition'
        && oldBindingRef === newBindingRef;
    });
  }
}

function findOldScopeElement(element, scopeTemplate, template) {
  var scopeName = scopeTemplate.type,
      id = scopeTemplate.id;

  if (scopeName === 'flowable:Connector') {
    return findExtension(element, 'flowable:Connector');
  }

  if (scopeName === 'bpmn:Error') {

    // (1) find by error event definition binding
    var errorEventDefinitionBinding = findErrorEventDefinitionBinding(template, id);

    if (!errorEventDefinitionBinding) {
      return;
    }

    // (2) find error event definition
    var errorEventDefinition = findOldBusinessObject(element, errorEventDefinitionBinding);

    if (!errorEventDefinition) {
      return;
    }

    // (3) retrieve referenced error
    return errorEventDefinition.errorRef;
  }
}

function isRootElementScope(scopeName) {
  return [ 'bpmn:Error' ].includes(scopeName);
}

function findOldScopeTemplate(scopeTemplate, oldTemplate) {
  var scopeName = scopeTemplate.type,
      scopeId = scopeTemplate.id,
      scopes = oldTemplate && handleLegacyScopes(oldTemplate.scopes);

  return scopes && find(scopes, function(scope) {

    if (isRootElementScope(scopeName)) {
      return scope.id === scopeId;
    }

    return scope.type === scopeName;
  });
}

function findErrorEventDefinitionBinding(template, templateErrorId) {
  return find(template.properties, function(property) {
    return property.binding.errorRef === templateErrorId;
  });
}

/**
 * Check whether property was changed after being set by template.
 *
 * @param {djs.model.Base|ModdleElement} element
 * @param {Object} oldProperty
 *
 * @returns {boolean}
 */
function propertyChanged(element, oldProperty) {
  var businessObject = getBusinessObject(element);

  var oldBinding = oldProperty.binding,
      oldBindingName = oldBinding.name,
      oldBindingType = oldBinding.type,
      oldPropertyValue = oldProperty.value,
      conditionExpression,
      definition;

  if (oldBindingType === 'property') {
    if (oldBindingName === 'conditionExpression') {
      conditionExpression = businessObject.get('bpmn:conditionExpression');

      return conditionExpression.get('bpmn:body') !== oldPropertyValue;
    }

    return businessObject.get(oldBindingName) !== oldPropertyValue;
  }

  if (oldBindingType === 'flowable:field') {
    return businessObject.get('flowable:string') !== oldPropertyValue;
  }

  if (oldBindingType === 'flowable:in') {
    if (oldBinding.expression) {
      return businessObject.get('sourceExpression') !== oldPropertyValue;
    } else {
      return businessObject.get('flowable:source') !== oldPropertyValue;
    }
  }

  if (oldBindingType === 'flowable:in:businessKey') {
    return businessObject.get('flowable:businessKey') !== oldPropertyValue;
  }

  if (oldBindingType === 'flowable:out') {
    return businessObject.get('flowable:target') !== oldPropertyValue;
  }

  if (oldBindingType === 'flowable:inputParameter') {
    if (oldBinding.scriptFormat) {
      definition = businessObject.get('flowable:definition');

      return definition && definition.get('flowable:value') !== oldPropertyValue;
    } else {
      return businessObject.get('flowable:value') !== oldPropertyValue;
    }
  }

  if (oldBindingType === 'flowable:outputParameter') {
    return businessObject.get('flowable:name') !== oldPropertyValue;
  }

  if (oldBindingType === 'flowable:property') {
    return businessObject.get('flowable:value') !== oldPropertyValue;
  }

  if (oldBindingType === 'flowable:errorEventDefinition') {
    return businessObject.get('expression') !== oldPropertyValue;
  }
}